/**
 * CookieConsent — GDPR-compliant cookie banner for Orixa Invest.
 *
 * Behaviour:
 *  - First render: bottom sheet asks user to Accept all / Reject non-essential / Manage preferences.
 *  - Choice persisted in localStorage under `orixa_cookie_consent` with a timestamp and version.
 *  - Auto-hides once a valid choice is stored. Never blocks navigation.
 *  - A `data-testid="cookie-reopen"` global event ("orixa:openCookies") reopens the modal so the
 *    footer link can invite users to change their consent at any time (required by GDPR).
 *  - Essential cookies (authentication, CSRF, checkout) are always enabled — the banner is
 *    purely informational for that category.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, ChartLineUp, Megaphone, X } from "@phosphor-icons/react";

const STORAGE_KEY = "orixa_cookie_consent";
const CONSENT_VERSION = "1.0";

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      prefs,
    }));
  } catch { /* noop */ }
  try {
    // Emit an event so any analytics loader can react to consent decisions.
    window.dispatchEvent(new CustomEvent("orixa:cookieConsent", { detail: prefs }));
  } catch { /* noop */ }
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [mode, setMode]       = useState("banner");    // "banner" | "settings"
  const [prefs, setPrefs]     = useState({ analytics: false, marketing: false });

  // First mount: show banner if no choice stored.
  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) setVisible(true);
    else setPrefs(stored.prefs || { analytics: false, marketing: false });
  }, []);

  // Footer link ("Cookie preferences") emits this global event to reopen the modal.
  useEffect(() => {
    const onReopen = () => { setMode("settings"); setVisible(true); };
    window.addEventListener("orixa:openCookies", onReopen);
    return () => window.removeEventListener("orixa:openCookies", onReopen);
  }, []);

  const acceptAll = useCallback(() => {
    const next = { analytics: true, marketing: true };
    setPrefs(next); writeConsent(next); setVisible(false);
  }, []);
  const rejectNonEssential = useCallback(() => {
    const next = { analytics: false, marketing: false };
    setPrefs(next); writeConsent(next); setVisible(false);
  }, []);
  const savePrefs = useCallback(() => {
    writeConsent(prefs); setVisible(false);
  }, [prefs]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent"
      className="fixed inset-x-0 bottom-0 z-[70] px-3 sm:px-6 pb-3 sm:pb-6 pointer-events-none"
    >
      <div className="pointer-events-auto max-w-4xl mx-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-start gap-3 sm:gap-5 p-4 sm:p-6">
          <div className="hidden sm:flex h-11 w-11 rounded-xl bg-primary/10 text-primary items-center justify-center flex-shrink-0">
            <Cookie size={22} weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-3">
              <h2 id="cookie-consent-title" className="font-display text-lg sm:text-xl tracking-tight leading-tight" data-testid="cookie-title">
                {mode === "banner" ? "We value your privacy." : "Cookie preferences"}
              </h2>
              <button
                type="button"
                onClick={rejectNonEssential}
                aria-label="Reject non-essential cookies and close"
                className="text-muted-foreground hover:text-foreground p-1 -m-1"
                data-testid="cookie-close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {mode === "banner" ? (
              <>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Orixa Invest uses <strong>essential cookies</strong> (authentication, security, checkout with Paystack)
                  to make the software work. Optional analytics or marketing cookies are only set with your consent.
                  You can change your choice any time via the <em>Cookie preferences</em> link in the footer.
                  Read our <Link to="/legal/privacy" className="text-primary underline underline-offset-2">Privacy Policy</Link>.
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90 card-elev"
                    data-testid="cookie-accept-all"
                  >
                    Accept all
                  </button>
                  <button
                    type="button"
                    onClick={rejectNonEssential}
                    className="inline-flex items-center justify-center gap-2 border border-border bg-card rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary"
                    data-testid="cookie-reject"
                  >
                    Reject non-essential
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("settings")}
                    className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
                    data-testid="cookie-manage"
                  >
                    Manage preferences
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                  Choose which optional cookie categories you allow. Essential cookies cannot be disabled — the
                  platform will not work without them.
                </p>
                <div className="mt-4 space-y-2.5" data-testid="cookie-settings">
                  <CategoryRow
                    icon={ShieldCheck}
                    title="Essential"
                    description="Authentication, CSRF protection, secure checkout with Paystack. Cannot be turned off."
                    locked
                    checked
                    testid="cookie-cat-essential"
                    onChange={() => {}}
                  />
                  <CategoryRow
                    icon={ChartLineUp}
                    title="Analytics"
                    description="Anonymous usage metrics that help us improve the platform."
                    checked={prefs.analytics}
                    testid="cookie-cat-analytics"
                    onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                  />
                  <CategoryRow
                    icon={Megaphone}
                    title="Marketing"
                    description="Only set if you engage with a marketing campaign. Never sold to third parties."
                    checked={prefs.marketing}
                    testid="cookie-cat-marketing"
                    onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                  />
                </div>
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={savePrefs}
                    className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90 card-elev"
                    data-testid="cookie-save"
                  >
                    Save preferences
                  </button>
                  <button
                    type="button"
                    onClick={acceptAll}
                    className="inline-flex items-center justify-center gap-2 border border-border rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary"
                    data-testid="cookie-save-all"
                  >
                    Accept all
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("banner")}
                    className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
                    data-testid="cookie-back"
                  >
                    Back
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ icon: Icon, title, description, checked, onChange, locked, testid }) {
  return (
    <div className="flex items-start gap-3 border border-border rounded-lg p-3 bg-secondary/30" data-testid={testid}>
      <Icon size={18} weight="duotone" className="text-primary mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm font-semibold">{title}</div>
          {locked ? (
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Always on</span>
          ) : (
            <label className="inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="sr-only"
                data-testid={`${testid}-input`}
              />
              <span className={`w-9 h-5 rounded-full relative transition-colors ${checked ? "bg-primary" : "bg-muted"}`}>
                <span className={`absolute top-0.5 left-0.5 h-4 w-4 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : ""}`} />
              </span>
            </label>
          )}
        </div>
        <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{description}</div>
      </div>
    </div>
  );
}
